import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Hidden,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Close, Delete, GetApp, InsertDriveFile } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import React, { createRef, useState } from "react";
import useAlerts from "../../Base/AlertProvider";
import {
  deleteRegistrationDocument,
  DocumentType,
  downloadRegistrationDocument,
  getRegistrationDocumentsForRegistrationId,
  uploadRegistrationDocument,
} from "../../Base/Services/RegistrationDocumentService";
import { makeStyles } from "@material-ui/core/styles";

const MAX_DOCUMENT_COUNT = 5;

const useStyles = makeStyles((theme) => ({
  responsiveDialog: {
    [theme.breakpoints.down("xs")]: {
      margin: 0,
      maxWidth: "100%",
      width: "100%",
      height: "100%",
      maxHeight: "none",
      borderRadius: 0,
    },
  },
  compactTitle: {
    [theme.breakpoints.down("xs")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  compactActions: {
    [theme.breakpoints.down("xs")]: {
      padding: "0.25rem 0.5rem",
    },
  },
  compactContent: {
    [theme.breakpoints.down("xs")]: {
      padding: "0.25rem 0.5rem",
    },
  },
}));

export default function ViewUploadHealthDocumentsDialog({ open, onClose, registrationId, ariaLabel, ...props }) {
  const [documents, setDocuments] = useState(null);
  const { addErrorAlert } = useAlerts();
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const fileInputRef = createRef();

  const classes = useStyles();

  React.useEffect(() => {
    if (open) {
      getRegistrationDocumentsForRegistrationId(registrationId, [DocumentType.MEDICAL_HISTORY]).then(setDocuments);
    } else {
      setDocuments(null);
    }
  }, [registrationId, open]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
      }}
      fullWidth={true}
      maxWidth="sm"
      aria-labelledby="medical-document-upload-dialog-title"
      aria-describedby="medical-document-upload-dialog-description"
      classes={{ paper: classes.responsiveDialog }}
      {...props}
    >
      <DialogTitle id="medical-document-upload-dialog-title" className={classes.compactTitle}>
        <span style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Documents / Medical History</span>
          <IconButton size="small" color="default" onClick={onClose} aria-label="close-dialog">
            <Close />
          </IconButton>
        </span>
      </DialogTitle>
      <DialogContent id="medical-document-upload-dialog-description" className={classes.compactContent}>
        <input
          type="file"
          id="file-to-upload-input"
          name="file-to-upload-input"
          hidden
          accept="image/*,.pdf"
          ref={fileInputRef}
          onChange={(e) => {
            const file = fileInputRef.current?.files ? fileInputRef.current?.files[0] : null;
            if (file) {
              const MAX_FILE_SIZE_MB = 25;
              if (file.size > 25 * 1000000) {
                addErrorAlert("Error uploading document. Maximum file size is " + MAX_FILE_SIZE_MB + "MB");
                return;
              }
              if (!file.type || !(file.type === "application/pdf" || file.type.startsWith("image/"))) {
                addErrorAlert("Invalid file type. Only PDF & image files are supported");
                return;
              }
              setUploadInProgress(true);
              uploadRegistrationDocument(registrationId, file, DocumentType.MEDICAL_HISTORY)
                .then((registrationDocument) => {
                  setDocuments([...documents, registrationDocument]);
                })
                .finally(() => {
                  setUploadInProgress(false);
                  e.target.value = "";
                });
            }
          }}
        />
        <Typography variant="body1" style={{ marginBottom: "0.5rem" }}>
          To share your medical history, you can fill out our{" "}
          <a
            target="_blank"
            href="https://vitalrecords.utah.gov/wp-content/uploads/Non-Identifying-Health-History.pdf"
            rel="noreferrer"
          >
            Non-Identifying Information for Adoption Registry Form
          </a>
          , and then upload it here, along with other documents & information you may like to share.
        </Typography>
        <Typography variant="caption">
          All Content uploaded by birth parents is User Content. User Content is not moderated by OVRS and is the sole
          responsibility of the registrant. OVRS is not responsible for the content or accuracy of any information
          shared.
        </Typography>

        <List>
          {documents &&
            documents.map((document, i) => (
              <ListItem key={i}>
                <Hidden xsDown>
                  <ListItemIcon>
                    <InsertDriveFile />
                  </ListItemIcon>
                </Hidden>
                <ListItemText
                  primary={
                    <span
                      style={{
                        textOverflow: "ellipsis",
                        maxWidth: "calc(100% - 80px)",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {document.documentName}
                    </span>
                  }
                ></ListItemText>
                <ListItemSecondaryAction>
                  <Tooltip title="Download File">
                    <IconButton onClick={() => downloadRegistrationDocument(document)}>
                      <GetApp />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove File">
                    <IconButton
                      onClick={() => {
                        if (window.confirm("Are you sure you want to remove this document?")) {
                          deleteRegistrationDocument(document).then(() => {
                            const updatedDocList = [...documents];
                            updatedDocList.splice(
                              updatedDocList.findIndex(
                                (doc) => document.registrationDocumentId === doc.registrationDocumentId
                              ),
                              1
                            );
                            setDocuments(updatedDocList);
                          });
                        }
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          {uploadInProgress && (
            <ListItem>
              <ListItemText
                primary={
                  <span>
                    <Skeleton style={{ width: "100%" }} />
                  </span>
                }
                secondary="Uploading"
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions className={classes.compactActions} style={{ display: "flex", justifyContent: "space-evenly" }}>
        <Button id="uploadDocumentsCloseBtn" color="default" variant="contained" onClick={onClose}>
          Close
        </Button>
        <FormHelperText>You are limited to uploading {MAX_DOCUMENT_COUNT} documents.</FormHelperText>
        <Button
          color="primary"
          variant="contained"
          disabled={uploadInProgress || (documents && documents.length >= MAX_DOCUMENT_COUNT)}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          Upload Document
        </Button>
      </DialogActions>
    </Dialog>
  );
}
